import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { productPages } from "../data/productPages";
import { usePopup } from "../context/PopupContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = productPages[slug];

  if (!product) {
    return (
      <main className="pt-28 pb-16 px-4 text-center">
        <h1 className="text-4xl text-white font-['Playfair_Display']">Product Not Found</h1>
        <Link to="/" className="text-purple-400 mt-4 inline-block">Go Home</Link>
      </main>
    );
  }

  return (
    <main data-testid="product-detail-page" className="pt-28 pb-16">
      <HeroSection product={product} />
      <AboutSection product={product} />
      <SmartFeaturesSection product={product} />
      <WhyChooseSection product={product} />
      <DetailedFeaturesSection product={product} />
      <CTASection product={product} />
      <FAQSection product={product} />
    </main>
  );
}

/* ─── Hero — centered with aurora + product image below ─── */
function HeroSection({ product }) {
  const { open } = usePopup();

  const productScreenshots = {
    chatro: "https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=900&h=500&fit=crop",
    edusphere: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=900&h=500&fit=crop",
    teamsync: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&h=500&fit=crop",
    clientstream: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&h=500&fit=crop",
  };
  const slug = Object.keys(productPages).find(k => productPages[k].name === product.name);

  return (
    <section className="relative pb-16 overflow-hidden">
      {/* Aurora bg */}
      <div className="absolute inset-0 bg-[#08080f]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[450px] bg-purple-600/20 rounded-full blur-[140px]" />
        <div className="absolute top-20 left-1/3 w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Text */}
      <div className="relative max-w-4xl mx-auto text-center pt-16 px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] text-white leading-tight mb-6">
            {product.tagline}
          </h1>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto mb-8">{product.subtitle}</p>
          <button onClick={open} className="inline-flex items-center gap-2 cta-button text-white px-10 py-4 rounded-full text-base font-semibold" data-testid="product-hero-cta">
            Get Started Now <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>

      {/* Product screenshot */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
        className="relative max-w-5xl mx-auto mt-16 px-4"
      >
        <div className="rounded-t-2xl overflow-hidden shadow-2xl shadow-purple-900/30 border border-purple-900/20 border-b-0">
          <img src={productScreenshots[slug]} alt={product.name} className="w-full h-[350px] sm:h-[450px] object-cover" loading="lazy" />
        </div>
      </motion.div>
    </section>
  );
}

/* ─── About — dark bg, centered ─── */
function AboutSection({ product }) {
  return (
    <section className="py-24 px-4 bg-[#08080f]">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white mb-6">
          {product.about.title.split(" ").slice(0, 3).join(" ")}{" "}
          <span className="gradient-text">{product.about.title.split(" ").slice(3).join(" ")}</span>
        </h2>
        <p className="text-gray-400 text-base leading-relaxed mb-4">{product.about.description}</p>
        <p className="text-gray-400 text-base leading-relaxed">{product.about.detail}</p>
      </div>
    </section>
  );
}

/* ─── Smart Features — white bg, left list + right image ─── */
function SmartFeaturesSection({ product }) {
  const featureImages = {
    chatro: "https://images.unsplash.com/photo-1577563908411-5077b6dc7624?w=600&h=600&fit=crop",
    edusphere: "https://images.unsplash.com/photo-1588702547923-7093a6c3ba33?w=600&h=600&fit=crop",
    teamsync: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=600&fit=crop",
    clientstream: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=600&fit=crop",
  };
  const slug = Object.keys(productPages).find(k => productPages[k].name === product.name);

  return (
    <section className="py-24 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          <span className="text-purple-600">Smart features</span> for smarter <span className="text-purple-600">results</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
          Experience advanced features designed for seamless operations and maximum efficiency.
        </p>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Product image - shows first on mobile */}
          <div className="rounded-2xl overflow-hidden shadow-xl lg:order-2">
            <img src={featureImages[slug]} alt="Features" className="w-full h-[250px] sm:h-[450px] object-cover" loading="lazy" />
          </div>

          {/* Feature list */}
          <div className="space-y-0 lg:order-1">
            {product.features.map((f, i) => (
              <div key={f.title} className={`py-6 ${i < product.features.length - 1 ? "border-b border-gray-200" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className="w-1 h-12 bg-purple-600 rounded-full flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-gray-900 font-bold text-lg mb-1">{f.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{f.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Why Choose — white bg, left 2x2 grid + right image ─── */
function WhyChooseSection({ product }) {
  const { open } = usePopup();
  const whyImages = {
    chatro: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=700&fit=crop",
    edusphere: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&h=700&fit=crop",
    teamsync: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=700&fit=crop",
    clientstream: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=700&fit=crop",
  };
  const slug = Object.keys(productPages).find(k => productPages[k].name === product.name);

  return (
    <section className="py-24 px-4 bg-[#08080f]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-14">
          Why choose <span className="gradient-text">{product.name}</span> for your business?
        </h2>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image - shows first on mobile */}
          <div className="rounded-2xl overflow-hidden shadow-2xl lg:order-2">
            <img src={whyImages[slug]} alt="Why choose" className="w-full h-[280px] sm:h-[500px] object-cover" loading="lazy" />
          </div>

          {/* 2x2 Grid / Slider on mobile */}
          <div className="lg:order-1 lg:grid lg:grid-cols-2 lg:gap-6 mobile-slider">
            {product.whyChoose.map((item, i) => (
              <div key={item.title} className="w-[240px] lg:w-auto bg-white/5 border border-purple-900/20 rounded-2xl p-6 card-hover">
                <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center mb-4">
                  <span className="text-purple-400 font-bold text-sm">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Detailed Features — accordion left + image right ─── */
function DetailedFeaturesSection({ product }) {
  const [openIdx, setOpenIdx] = useState(0);
  const detailImages = {
    chatro: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=600&h=600&fit=crop",
    edusphere: "https://images.unsplash.com/photo-1610484826967-09c5720778c7?w=600&h=600&fit=crop",
    teamsync: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=600&fit=crop",
    clientstream: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=600&fit=crop",
  };
  const slug = Object.keys(productPages).find(k => productPages[k].name === product.name);

  return (
    <section className="py-24 px-4 bg-[#f8f8fc]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-14">
          Features to power <span className="text-purple-600">every workflow</span>
        </h2>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image - shows first on mobile */}
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img src={detailImages[slug]} alt="Features" className="w-full h-[250px] sm:h-[450px] object-cover" loading="lazy" />
          </div>

          {/* Accordion */}
          <div className="space-y-0">
            {product.detailedFeatures.map((f, i) => (
              <div key={f.title} className={`border-b border-gray-200 ${i === 0 ? "border-t" : ""}`}>
                <button
                  onClick={() => setOpenIdx(openIdx === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left"
                >
                  <span className={`font-semibold text-lg ${openIdx === i ? "text-purple-600" : "text-gray-900"}`}>{f.title}</span>
                  <ChevronDown size={18} className={`transition-transform flex-shrink-0 ${openIdx === i ? "rotate-180 text-purple-600" : "text-gray-400"}`} />
                </button>
                {openIdx === i && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{f.description}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── CTA Banner — gradient with image ─── */
function CTASection({ product }) {
  const { open } = usePopup();
  const ctaImages = {
    chatro: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=500&h=400&fit=crop",
    edusphere: "https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=500&h=400&fit=crop",
    teamsync: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=400&fit=crop",
    clientstream: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500&h=400&fit=crop",
  };
  const slug = Object.keys(productPages).find(k => productPages[k].name === product.name);

  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-gradient-to-r from-purple-700 via-indigo-700 to-purple-800 rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-2 items-center">
            <div className="p-6 sm:p-10 lg:p-14 text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white mb-4">{product.ctaText}</h2>
              <p className="text-white/80 text-sm sm:text-base mb-8">{product.ctaDescription}</p>
              <button onClick={open} className="inline-flex items-center gap-2 bg-white text-purple-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-100 transition-colors">
                Get Started for Free <ArrowRight size={18} />
              </button>
            </div>
            <div className="hidden lg:block">
              <img src={ctaImages[slug]} alt="CTA" className="w-full h-[350px] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ — dark bg ─── */
function FAQSection({ product }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-24 px-4 bg-[#08080f]">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-14">
          Frequently asked <span className="gradient-text">questions</span>
        </h2>
        <div className="space-y-4">
          {product.faqs.map((faq, i) => (
            <div key={i} className="border-b border-purple-900/20">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left"
                data-testid={`faq-${i}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
                  <span className="text-white font-medium text-base">{faq.q}</span>
                </div>
                <ChevronRight size={18} className={`transition-transform flex-shrink-0 ml-4 ${openIndex === i ? "rotate-90 text-purple-400" : "text-gray-500"}`} />
              </button>
              {openIndex === i && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pb-5 pl-7">
                  <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
